<template>
  <div class="traffic-modal-content">
    <div class="traffic-header">
      <div class="traffic-title">일별 전체 트래픽 현황</div>
      <div class="controls">
        <el-select 
          v-model="selectedDays" 
          size="default" 
          style="width: 120px"
          @change="handleDaysChange"
        >
          <el-option label="7일" :value="7" />
          <el-option label="15일" :value="15" />
          <el-option label="30일" :value="30" />
          <el-option label="60일" :value="60" />
          <el-option label="90일" :value="90" />
        </el-select>
        <el-button @click="toggleTrafficMode" :loading="loading">
          {{ trafficModeButtonText }}
        </el-button>
        <el-button @click="calendarDialogVisible = true">
          등록 캘린더
        </el-button>
      </div>
    </div>

    <div v-loading="loading" class="chart-container">
      <Line :data="chartData" :options="chartOptions" v-if="!loading && currentDataCount > 0" />
      <el-empty v-if="!loading && currentDataCount === 0" description="트래픽 데이터가 없습니다" />
    </div>

    <div class="summary-stats" v-if="currentDataCount > 0">
      <div class="stat-card">
        <div class="stat-label">총 조회수</div>
        <div class="stat-value">{{ totalViews.toLocaleString() }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">일평균 조회수</div>
        <div class="stat-value">{{ averageViews.toLocaleString() }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">최고 조회수</div>
        <div class="stat-value">{{ maxViews.toLocaleString() }}</div>
      </div>
    </div>

    <el-dialog
      v-model="calendarDialogVisible"
      title="등록 캘린더"
      width="1100px"
      top="4vh"
      class="register-calendar-dialog"
      destroy-on-close
    >
      <div class="calendar-dialog-body">
        <RegisterCalendarPanel />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { ElMessage } from 'element-plus';
import { getTotalTraffic, getN2nMarketTraffic } from '@/services/manager';
import RegisterCalendarPanel from '@/components/modal/RegisterCalendarPanel.vue';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default {
  name: 'TrafficModal',
  components: {
    Line,
    RegisterCalendarPanel
  },
  props: {
    defaultDays: {
      type: Number,
      default: 30
    }
  },
  setup(props) {
    const loading = ref(false);
    const calendarDialogVisible = ref(false);
    const viewMode = ref('total');
    const trafficData = ref([]);
    const n2nMarketData = ref([]);
    const selectedDays = ref(props.defaultDays);

    const getVisibleTotalTraffic = (item) => {
      return item?.totalViews || 0;
    };

    const marketDateRows = computed(() => {
      if (n2nMarketData.value.length === 0) return [];
      const dateMap = new Map();
      n2nMarketData.value.forEach((item) => {
        const date = item.date;
        const prev = dateMap.get(date) || 0;
        dateMap.set(date, prev + (item.totalViews || 0));
      });
      return [...dateMap.entries()]
        .map(([date, total]) => ({ date, total }))
        .sort((a, b) => a.date.localeCompare(b.date));
    });

    const currentSeries = computed(() => {
      if (viewMode.value === 'market') {
        return marketDateRows.value.map((item) => item.total || 0);
      }
      return trafficData.value.map((item) => getVisibleTotalTraffic(item));
    });

    const currentDataCount = computed(() => {
      if (viewMode.value === 'market') {
        return marketDateRows.value.length;
      }
      return trafficData.value.length;
    });

    const trafficModeButtonText = computed(() => {
      return viewMode.value === 'total' ? '마켓별조회수' : '전체트래픽';
    });

    // 통계 계산
    const totalViews = computed(() => {
      return currentSeries.value.reduce((sum, value) => sum + value, 0);
    });

    const averageViews = computed(() => {
      if (currentSeries.value.length === 0) return 0;
      return Math.round(totalViews.value / currentSeries.value.length);
    });

    const maxViews = computed(() => {
      if (currentSeries.value.length === 0) return 0;
      return Math.max(...currentSeries.value);
    });

    // 차트 데이터
    const chartData = computed(() => {
      if (viewMode.value === 'market') {
        const dateMap = new Map();
        const marketMap = new Map();

        n2nMarketData.value.forEach((item) => {
          const date = item.date;
          const marketKey = String(item.marketNumber ?? 'unknown');
          const marketMemo = item.marketMemo || '';

          if (!dateMap.has(date)) {
            dateMap.set(date, {});
          }

          const dayRow = dateMap.get(date);
          dayRow[marketKey] = (dayRow[marketKey] || 0) + (item.totalViews || 0);
          marketMap.set(marketKey, {
            key: marketKey,
            number: Number(item.marketNumber) || 0,
            label: marketMemo ? `${item.marketNumber} ${marketMemo}` : `${item.marketNumber}`
          });
        });

        const labels = [...dateMap.keys()].sort((a, b) => a.localeCompare(b));
        const markets = [...marketMap.values()].sort((a, b) => a.number - b.number);
        const colors = [
          '#409EFF',
          '#67C23A',
          '#E6A23C',
          '#F56C6C',
          '#8B5CF6',
          '#14B8A6',
          '#EC4899',
          '#6366F1',
          '#F97316',
          '#10B981',
          '#0EA5E9',
          '#A855F7',
          '#84CC16'
        ];

        return {
          labels,
          datasets: markets.map((market, index) => ({
            label: market.label,
            data: labels.map((date) => dateMap.get(date)?.[market.key] || 0),
            borderColor: colors[index % colors.length],
            backgroundColor: `${colors[index % colors.length]}22`,
            fill: false,
            tension: 0.35
          }))
        };
      }

      const reversedData = [...trafficData.value].reverse();
      
      return {
        labels: reversedData.map(item => item.date),
        datasets: [
          {
            label: '전체',
            data: reversedData.map(item => getVisibleTotalTraffic(item)),
            borderColor: '#409EFF',
            backgroundColor: 'rgba(64, 158, 255, 0.2)',
            fill: true,
            tension: 0.4,
            borderWidth: 3
          }
        ]
      };
    });

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              return value.toLocaleString();
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    };

    // 데이터 로드
    const loadTotalTrafficData = async () => {
      loading.value = true;
      try {
        const response = await getTotalTraffic({ days: selectedDays.value });
        
        if (response.success) {
          trafficData.value = response.data || [];
        } else {
          throw new Error(response.message || '트래픽 데이터 로드 실패');
        }
      } catch (error) {
        console.error('트래픽 데이터 로드 실패:', error);
        ElMessage.error(error.response?.data?.message || '트래픽 데이터 로드 중 오류가 발생했습니다.');
        trafficData.value = [];
      } finally {
        loading.value = false;
      }
    };

    const loadN2nMarketData = async () => {
      loading.value = true;
      try {
        const response = await getN2nMarketTraffic({ days: selectedDays.value });

        if (response.success) {
          n2nMarketData.value = response.data || [];
        } else {
          throw new Error(response.message || '상품등록 마켓별 조회수 로드 실패');
        }
      } catch (error) {
        console.error('상품등록 마켓별 조회수 로드 실패:', error);
        ElMessage.error(error.response?.data?.message || '상품등록 마켓별 조회수 로드 중 오류가 발생했습니다.');
        n2nMarketData.value = [];
      } finally {
        loading.value = false;
      }
    };

    const loadCurrentTrafficData = async () => {
      if (viewMode.value === 'market') {
        await loadN2nMarketData();
        return;
      }
      await loadTotalTrafficData();
    };

    const toggleTrafficMode = async () => {
      viewMode.value = viewMode.value === 'total' ? 'market' : 'total';
      await loadCurrentTrafficData();
    };

    const handleDaysChange = () => {
      loadCurrentTrafficData();
    };

    watch(() => props.defaultDays, (newVal) => {
      selectedDays.value = newVal;
      loadCurrentTrafficData();
    });

    onMounted(() => {
      loadCurrentTrafficData();
    });

    return {
      loading,
      calendarDialogVisible,
      viewMode,
      trafficData,
      n2nMarketData,
      selectedDays,
      chartData,
      chartOptions,
      currentDataCount,
      trafficModeButtonText,
      totalViews,
      averageViews,
      maxViews,
      handleDaysChange,
      toggleTrafficMode
    };
  }
};
</script>

<style scoped>
.traffic-modal-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.traffic-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-sm);
}

.traffic-title {
  font-size: var(--el-font-size-large);
  font-weight: var(--el-font-weight-bold);
  color: var(--el-text-color-primary);
}

.controls {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-sm);
}

.chart-container {
  min-height: 320px;
  height: 320px;
  width: 100%;
  position: relative;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-md);
}

.stat-card {
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: center;
}

.stat-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
  margin-bottom: var(--spacing-xs);
}

.stat-value {
  font-size: var(--el-font-size-large);
  font-weight: var(--el-font-weight-bold);
  color: var(--el-color-primary);
}

.calendar-dialog-body {
  max-height: calc(100vh - 140px);
  overflow: auto;
}

.register-calendar-dialog :deep(.el-dialog__body) {
  padding-top: 12px;
}

@media (max-width: 768px) {
  .chart-container {
    min-height: 260px;
    height: 260px;
  }
  
  .summary-stats {
    grid-template-columns: 1fr;
  }
}
</style>

